import React, { useState } from 'react'
import { PatientHistoryType } from 'src/graphql.generated'
import { DISCHARGE_SUMMARY_NOTE_FORM_SCHEMA } from 'src/participantHistoryForm/form/dischargeSummaryNote/DischargeSummaryNoteFormSchema'
import { useDraftSaver, useRestoredDraft } from '../../../../api/historyDraft'
import { usePatientHistoryPrefill } from '../../../../api/usePatientHistoryPrefill'
import { useSubmitPatientHistory } from '../../../../api/useSubmitPatientHistory'
import { BillingCode } from '../billingCodes'
import BillingCodesSelect from '../BillingCodesSelect'
import {
  DraftNotice,
  FieldGrid,
  FieldSection,
  FormActions,
  LabeledDate,
  LabeledRadioGroup,
  LabeledText,
  MissingFields,
  todayIso,
  useRequiredFields,
} from '../../../fieldPrimitives'
import { REASON_OPTIONS, ReasonForDischarge } from '../../historyOptions'
import { layoutForType } from '../../historyLayouts'
import PrefillLoading from '../PrefillLoading'
import { ValidationIssues, validationIssues } from '../validation'
import { codesAt, textAt } from '../valuePath'

const DischargeSummaryNoteForm = ({ uid, prefill }: { uid: string; prefill: Record<string, unknown> }) => {
  const restored = useRestoredDraft(uid, PatientHistoryType.DischargeSummaryNote)
  const source = restored ?? prefill
  const [firstName, setFirstName] = useState(textAt(source, 'participant.firstName'))
  const [lastName, setLastName] = useState(textAt(source, 'participant.lastName'))
  const [memberID, setMemberID] = useState(textAt(source, 'participant.memberID'))
  const [practitionerName, setPractitionerName] = useState(textAt(source, 'assessingPractitioner.name'))
  const [licenseType, setLicenseType] = useState(textAt(source, 'assessingPractitioner.licenseType'))
  const [dischargeDate, setDischargeDate] = useState(textAt(restored, 'dischargeDate') || todayIso())
  const [billingCodes, setBillingCodes] = useState(codesAt(source, 'billingCodes') as BillingCode[])
  const [reason, setReason] = useState(textAt(restored, 'reasonForDischarge.reason') as ReasonForDischarge | '')
  const [reasonDetails, setReasonDetails] = useState(textAt(restored, 'reasonForDischarge.details'))
  const [smartGoals, setSmartGoals] = useState(textAt(source, 'treatmentPlan.smartGoals'))
  const [goalsMet, setGoalsMet] = useState(textAt(restored, 'treatmentPlan.goalsMet') as 'yes' | 'no' | '')
  const [goalsMetDetails, setGoalsMetDetails] = useState(textAt(restored, 'treatmentPlan.details'))
  const [issues, setIssues] = useState<string[]>([])
  const { submit, submitting, error } = useSubmitPatientHistory(uid)

  const required = useRequiredFields({
    'First Name': Boolean(firstName.trim()),
    'Last Name': Boolean(lastName.trim()),
    'Insurance member ID': Boolean(memberID.trim()),
    'Practitioner name': Boolean(practitionerName.trim()),
    'License Type': Boolean(licenseType.trim()),
    'Discharge Date': Boolean(dischargeDate),
    'Billing codes': billingCodes.length > 0,
    'Reason for Discharge': Boolean(reason),
    'Goals Met': Boolean(goalsMet),
    ...(goalsMet === 'no' && { 'Goals met details': Boolean(goalsMetDetails.trim()) }),
  })

  const value = {
    date: todayIso(),
    dischargeDate,
    billingCodes,
    participant: { firstName, lastName, memberID },
    assessingPractitioner: { name: practitionerName, licenseType },
    treatmentPlan: { smartGoals, goalsMet, details: goalsMetDetails },
    reasonForDischarge: { reason, details: reasonDetails },
  }
  const dirty = useDraftSaver(uid, PatientHistoryType.DischargeSummaryNote, value)

  const onSubmit = async () => {
    if (!required.attempt()) {
      return
    }

    const { date } = value
    const found = validationIssues(
      DISCHARGE_SUMMARY_NOTE_FORM_SCHEMA,
      value,
      layoutForType(PatientHistoryType.DischargeSummaryNote)
    )

    setIssues(found)

    if (found.length === 0) {
      await submit({ date, type: PatientHistoryType.DischargeSummaryNote, value })
    }
  }

  return (
    <FieldSection>
      <FieldSection title="Overall Information">
        <FieldSection title="Participant">
          <FieldGrid>
            <LabeledText
              label="First Name"
              value={firstName}
              onChange={setFirstName}
              required
              error={required.error('First Name')}
            />
            <LabeledText
              label="Last Name"
              value={lastName}
              onChange={setLastName}
              required
              error={required.error('Last Name')}
            />
            <LabeledText
              label="Insurance member ID"
              value={memberID}
              onChange={setMemberID}
              required
              error={required.error('Insurance member ID')}
            />
          </FieldGrid>
        </FieldSection>
        <FieldSection title="Assessing Practitioner">
          <FieldGrid>
            <LabeledText
              label="Name"
              value={practitionerName}
              onChange={setPractitionerName}
              required
              error={required.error('Practitioner name')}
            />
            <LabeledText
              label="License Type"
              value={licenseType}
              onChange={setLicenseType}
              required
              error={required.error('License Type')}
            />
          </FieldGrid>
        </FieldSection>
        <FieldSection title="Discharge Date">
          <FieldGrid>
            <LabeledDate
              label="Discharge Date"
              value={dischargeDate}
              onChange={setDischargeDate}
              required
              error={required.error('Discharge Date')}
            />
          </FieldGrid>
        </FieldSection>
      </FieldSection>

      <FieldSection title="Billing and Diagnosis Codes">
        <BillingCodesSelect
          value={billingCodes}
          onChange={setBillingCodes}
          required
          error={required.error('Billing codes')}
        />
      </FieldSection>

      <FieldSection title="Reason for Discharge">
        <LabeledRadioGroup
          label="Reason for discharge"
          value={reason}
          onChange={setReason}
          options={REASON_OPTIONS}
          required
          error={required.error('Reason for Discharge')}
        />
        <LabeledText
          label="Details"
          value={reasonDetails}
          onChange={setReasonDetails}
          placeholder="Brief note with applicable info on program engagement overall"
          multiline
        />
      </FieldSection>

      <FieldSection title="Progress Assessment">
        <FieldSection title="SMART Goals">
          <LabeledText label="SMART goals" value={smartGoals} onChange={setSmartGoals} multiline />
        </FieldSection>
        <FieldSection title="Goals Met">
          <LabeledRadioGroup
            label=""
            value={goalsMet}
            onChange={setGoalsMet}
            required
            error={required.error('Goals Met')}
            options={[
              { value: 'yes', label: 'Yes' },
              { value: 'no', label: 'No' },
            ]}
          />
          {goalsMet === 'no' && (
            <LabeledText
              label="Details"
              value={goalsMetDetails}
              onChange={setGoalsMetDetails}
              multiline
              required
              error={required.error('Goals met details')}
            />
          )}
        </FieldSection>
      </FieldSection>

      <MissingFields labels={required.missing} />
      <ValidationIssues issues={issues} />
      <DraftNotice restored={restored !== null} dirty={dirty} />
      <FormActions onSubmit={onSubmit} disabled={false} submitting={submitting} error={error} />
    </FieldSection>
  )
}

const DischargeSummaryNoteFields = ({ uid }: { uid: string }) => {
  const { loading, value } = usePatientHistoryPrefill(uid, PatientHistoryType.DischargeSummaryNote)

  return loading ? <PrefillLoading /> : <DischargeSummaryNoteForm uid={uid} prefill={value} />
}

export default DischargeSummaryNoteFields
